<template>
    <div class="flex flex-col min-h-0 h-full p-6 pt-5 | 2xl:p-8 2xl:pt-8">
        <page-header :subtitle="pageSubtitle" :title="pageTitle" />

        <main class="flex flex-col min-h-0 h-full rounded shadow-lg bg-white">
            <div class="border-b border-gray-200 rounded-t bg-white">
                <import-steps :path-prefix="pathPrefix" />
            </div>

            <div class="flex flex-col justify-center w-full h-full overflow-y-auto p-8">
                <file-upload
                    :file-types="['csv', 'xlsx']"
                    :is-uploading="isUploading"
                    @error="hasError = $event"
                    v-model="file"
                />
            </div>

            <div class="flex justify-between border-t border-gray-200 rounded-b bg-white">
                <div class="flex-1 flex items-center justify-end p-4 space-x-2">
                    <nio-button
                        :disabled="isUploading"
                        icon="regular/remove"
                        @click="cancel"
                    >
                        {{ $t('global.general.cancel') }}
                    </nio-button>
                    <nio-button
                        color="blue"
                        :disabled="!hasFile || hasError"
                        icon="regular/upload-bottom"
                        :loading="isUploading"
                        @click="uploadFile"
                    >
                        {{ $t('imports.upload.upload') }}
                    </nio-button>
                </div>
            </div>
        </main>
    </div>
</template>

<script>
    import Vapor from 'laravel-vapor';
    import { mapState, mapMutations } from 'vuex';
    import FileUpload from '@/components/FileUpload.vue';
    import ImportSteps from '@/components/ImportSteps.vue';
    import PageHeader from '@/components/PageHeader.vue';

    export default {
        components: {
            FileUpload,
            ImportSteps,
            PageHeader,
        },

        props: {
            importType: {
                type: String,
                required: true,
            },
            pathPrefix: {
                type: String,
                required: true,
            },
            pageSubtitle: {
                type: String,
                default: '',
            },
        },

        data() {
            return {
                file: null,
                isUploading: false,
                hasError: false,
            };
        },

        computed: {
            ...mapState([
                'authUser',
            ]),

            pageTitle() {
                return this.$t(`imports.pageTitle.${this.pathPrefix}`);
            },

            hasFile() {
                return !!this.file;
            },
        },

        methods: {
            ...mapMutations('appointment', [
                'setCurrentImport',
                'setPreviewRows',
            ]),

            cancel() {
                if (this.isUploading) {
                    return;
                }

                this.$emit('cancel');
            },

            async uploadFile() {
                if (!this.hasFile || this.isUploading) {
                    return;
                }

                this.isUploading = true;

                const s3Response = await Vapor.store(this.file, {
                    bucket: process.env.VUE_APP_S3_BUCKET,
                    baseURL: this.$axios.defaults.baseURL,
                    headers: this.$axios.defaults.headers.common,
                });

                const payload = {
                    account_id: this.authUser.account.id,
                    type: this.importType,
                    file_uuid: s3Response.uuid,
                    file_key: s3Response.key,
                    file_bucket: s3Response.bucket,
                    file_name: this.file.name,
                    file_content_type: this.file.type,
                };

                if (this.importType == 'campaign') {
                    payload.campaign_id = this.$route.params.campaignId;
                }

                try {
                    const response = await this.$axios.post('imports', payload);
                    const newData = response.data.data;

                    this.setCurrentImport(newData.import);
                    this.setPreviewRows(newData.previewRows);

                    this.$emit('upload', newData.import.id);

                    this.isUploading = false;
                } catch (error) {
                    this.isUploading = false;

                    this.$notify.show({
                        type: 'error',
                        message: this.$t('imports.upload.uploadError'),
                    });

                    throw error;
                }
            },
        },
    };
</script>
